import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { debug } from "../lib/debug.js";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { useActiveFlights, AIRPORTS, getReputationTier, calculateCancellationCost } from "../components/sim/GameLoopEngine.jsx";
import { useAuth } from "../components/auth";
import NegotiationController, { createNegotiationData } from "../components/negotiations/NegotiationController.jsx";
// Live weather from Tomorrow.io - no more simulated weather
import { getWeatherStatusColor, getWeatherStatusLabel } from "../systems/WeatherImpact.js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
// CheatConsole moved to Layout.jsx for global access
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import TutorialController from '../components/Tutorial/TutorialController.jsx';

// Command Console (hamburger menu)
import { CommandConsole } from "../components/CommandConsole.jsx";
import { RouteNetworkPanel } from "../components/panels/RouteNetworkPanel.jsx";
import { ConsultantsPanel } from "../components/panels/ConsultantsPanel.jsx";
import { CrewMarketPanel } from "../components/panels/CrewMarketPanel.jsx";
import { TechSystemsPanel } from "../components/panels/TechSystemsPanel.jsx";
import { SupportCrewPanel } from "../components/panels/SupportCrewPanel.jsx";
import { AdvertisingPanel } from "../components/panels/AdvertisingPanel.jsx";
import { RebrandingPanel } from "../components/panels/RebrandingPanel.jsx";
import { InnerCirclePanel } from "../components/panels/InnerCirclePanel.jsx";
import { canManuallyEndDay } from "../systems/EndOfDaySystem.js";
import { HUB_CAPACITY } from "../components/simple/ExclusiveHubOffer.jsx";

// System functions for panel actions
import { hireConsultant, fireConsultant } from "../systems/ConsultantsSystem.js";
import { hireCrew, fireCrew } from "../systems/CrewMarketSystem.js";
import { purchaseTech } from "../systems/TechSystems.js";
import { hireSupportCrew, fireSupportCrew } from "../systems/SupportCrewSystem.js";
import { launchCampaign, cancelCampaign } from "../systems/AdvertisingSystem.js";
import { executeRebrand } from "../systems/RebrandingSystem.js";
import {
  getNextNegotiationAirport,
} from "../systems/EndOfDaySystem.js";
import { getEOSStatus, EOS_REQUIREMENTS } from "../systems/EOSSystem.js";
import { RevenueTickerCompact } from "../components/ui/RevenueTicker.jsx";
import OnboardingFlow from "../components/onboarding/OnboardingFlow.jsx";
import { RadioToggle } from "../components/ui/RadioPlayer.jsx";
import { SplitFlapDisplay } from "../components/splitflap/SplitFlapDisplay.jsx";
import { FuelPriceTicker } from "../components/fuel/FuelPriceTicker.jsx";
import { FuelPanel } from "../components/fuel/FuelPanel.jsx";
import FleetUnlockModal from "../components/modals/FleetUnlockModal.jsx";
// Gate announcements archived - simulation mode only

// Icons removed - text-only UI

// ============================================================================
// PHASE CONFIGURATION - AUTOMATIC PROGRESSION WITH ATC CALLOUTS
// ============================================================================

// Rich ATC callouts for immersive ground operations
// {flightNumber}, {destination}, {gate}, {runway}, {aircraft} are replaced dynamically
const ATC_CALLOUTS = {
  preboard: {
    start: [
      "Good afternoon, ladies and gentlemen. We'd like to welcome passengers requiring extra assistance, as well as families with small children, to begin boarding flight {flightNumber} to {destination}.",
      "Attention passengers of {flightNumber}. We are now beginning our pre-boarding process for those needing additional time or assistance.",
      "This is a pre-boarding announcement for flight {flightNumber} with service to {destination}. Passengers requiring wheelchair assistance and families with children under 5 may now approach the gate.",
    ],
    progress: [
      "Pre-board passengers, please make your way down the jetway. A crew member will assist you to your seats.",
      "Thank you for your patience. We'll begin general boarding momentarily.",
      "Pre-boarding continuing for flight {flightNumber}. General boarding will begin in approximately 5 minutes.",
    ],
  },
  boarding: {
    start: [
      "Now boarding flight {flightNumber} to {destination}. All passengers may now board. Please have your boarding pass ready for scanning.",
      "General boarding has begun for flight {flightNumber}. All remaining passengers please proceed to the gate.",
      "Attention all passengers: flight {flightNumber} is now boarding at gate {gate}. Please have your boarding pass and ID ready.",
      "We are now boarding all rows and all zones for flight {flightNumber} with service to {destination}. Welcome aboard!",
    ],
    progress: [
      "Boarding continues for flight {flightNumber}. Please find your seat and stow your carry-on luggage in the overhead bins.",
      "For passengers still in the terminal, please make your way to gate {gate} immediately. Flight {flightNumber} is in final boarding.",
      "If you're traveling on flight {flightNumber}, please board now. We'll be closing the door shortly.",
      "Welcome aboard! Please take your seats quickly as we prepare for an on-time departure.",
    ],
    finalCall: [
      "This is the final boarding call for flight {flightNumber} to {destination}. All remaining passengers should board immediately or they will be denied boarding.",
      "Final call for {flightNumber}. The boarding door is closing in 2 minutes. Please board now.",
      "Last call for passengers on flight {flightNumber}. If you are in the terminal, please proceed immediately to gate {gate}.",
    ],
  },
  doorclose: {
    start: [
      "Flight attendants, please prepare the cabin for departure. Cross-check and arm doors.",
      "Ground crew, this is {flightNumber}. Requesting door closure and jetway retraction.",
      "Ladies and gentlemen, the boarding door is now closing. Please ensure your seatbelt is fastened and your tray table is stowed.",
    ],
    progress: [
      "All doors are now closed and armed. Cabin crew preparing for departure.",
      "Jetway has been retracted. Standby for pushback clearance.",
      "Door closure complete. {flightNumber} requesting pushback clearance.",
    ],
  },
  pushback: {
    start: [
      "Ground, {flightNumber} at gate {gate} requesting pushback, spot 1 approved.",
      "{flightNumber}, cleared for pushback, expect runway {runway}, altimeter 30.12.",
      "Tug is connected, parking brake released. Pushing back from gate {gate}.",
      "Ground crew, {flightNumber} pushing back. Clear left, clear right.",
    ],
    progress: [
      "Pushback in progress. Starting engine number 2.",
      "Both engines started and stable. Completing pushback.",
      "{flightNumber} pushback complete, request taxi.",
      "Disconnect the tug. {flightNumber} ready for taxi.",
    ],
  },
  taxiout: {
    start: [
      "Ground, {flightNumber} ready to taxi.",
      "{flightNumber}, taxi to runway {runway} via Alpha, hold short of runway.",
      "Taxiing to runway {runway}. Estimated wheels up in 4 minutes.",
      "{flightNumber} cleared to taxi, runway {runway} via taxiway Bravo.",
    ],
    progress: [
      "Cabin crew, please be seated for takeoff.",
      "Ladies and gentlemen, we're number 2 for departure. Flight attendants, prepare for takeoff.",
      "{flightNumber} holding short of runway {runway}.",
      "Tower, {flightNumber} ready for departure runway {runway}.",
    ],
    cleared: [
      "{flightNumber}, cleared for takeoff runway {runway}, winds 270 at 12.",
      "Tower clears {flightNumber} for immediate takeoff. Have a good flight.",
      "{flightNumber} rolling. See you at flight level 350.",
      "Takeoff thrust set. V1, rotate. Positive rate, gear up.",
    ],
  },
};

const GROUND_PHASES = {
  preboard: {
    name: "Pre-boarding",
    abbrev: "PRE",
    color: "#3b82f6",
    bgColor: "#1e3a5f",
    minutesBefore: 30,
    durationMinutes: 8,
  },
  boarding: {
    name: "Boarding",
    abbrev: "BRD",
    color: "#eab308",
    bgColor: "#4a3f0f",
    minutesBefore: 22,
    durationMinutes: 14,
  },
  doorclose: {
    name: "Door Closure",
    abbrev: "CLO",
    color: "#ef4444",
    bgColor: "#4a1515",
    minutesBefore: 8,
    durationMinutes: 4,
  },
  pushback: {
    name: "Pushback",
    abbrev: "PSH",
    color: "#f97316",
    bgColor: "#4a2a0f",
    minutesBefore: 4,
    durationMinutes: 2,
  },
  taxiout: {
    name: "Taxi",
    abbrev: "TAX",
    color: "#22c55e",
    bgColor: "#0f4a2a",
    minutesBefore: 2,
    durationMinutes: 2,
  },
};

const PHASE_ORDER = ["preboard", "boarding", "doorclose", "pushback", "taxiout"];

// Generate a callout for a given phase and progress
function getCalloutForPhase(phaseKey, progress, flight) {
  const callouts = ATC_CALLOUTS[phaseKey];
  if (!callouts) return null;

  let pool;
  if (progress < 0.2) {
    pool = callouts.start;
  } else if (progress < 0.8) {
    pool = callouts.progress || callouts.start;
  } else if (phaseKey === "boarding" && callouts.finalCall) {
    pool = callouts.finalCall;
  } else if (phaseKey === "taxiout" && callouts.cleared) {
    pool = callouts.cleared;
  } else {
    pool = callouts.progress || callouts.start;
  }

  if (!pool || pool.length === 0) return null;

  // Use flight ID to pick a consistent callout (not random each render)
  const idx = Math.abs(hashCode(flight.id + phaseKey + Math.floor(progress * 3))) % pool.length;
  let text = pool[idx];

  // Replace placeholders
  const gate = "B" + ((hashCode(flight.id) % 30) + 1);
  const runways = ["09L", "27R", "04", "22", "36L", "18R", "10", "28"];
  const runway = runways[Math.abs(hashCode(flight.destination)) % runways.length];

  text = text
    .replace(/\{flightNumber\}/g, flight.flightNumber)
    .replace(/\{destination\}/g, AIRPORTS[flight.destination]?.city || flight.destination)
    .replace(/\{gate\}/g, gate)
    .replace(/\{runway\}/g, runway)
    .replace(/\{aircraft\}/g, flight.aircraftType || "aircraft");

  return text;
}

// Simple hash function for deterministic randomness
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash;
}
const AIRBORNE_PHASES = ["takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in", "arrived", "completed"];

// ============================================================================
// NEWS TICKER DATA (fallback if no macro news)
// ============================================================================

const FALLBACK_NEWS = [
  { type: "alert", text: "WEATHER: Thunderstorms developing over midwest - expect delays at ORD, MDW" },
  { type: "alert", text: "NOTICE: Ground stop lifted at JFK - normal operations resuming" },
  { type: "fuel", text: "FUEL: Jet fuel prices up 8% following Middle East tensions" },
  { type: "alert", text: "WEATHER: Dense fog advisory at SFO - CAT III approaches required" },
  { type: "fuel", text: "FUEL: Prices stabilize after refinery comes back online" },
  { type: "industry", text: "INDUSTRY: Delta reports record Q3 profits, announces new routes to Europe" },
  { type: "industry", text: "INDUSTRY: Boeing delays 787 deliveries citing supply chain issues" },
  { type: "industry", text: "INDUSTRY: FAA proposes new crew rest requirements for ultra-long-haul flights" },
  { type: "industry", text: "INDUSTRY: United places order for 100 new A321XLR aircraft" },
  { type: "industry", text: "INDUSTRY: Southwest pilots ratify new contract with 30% pay increase" },
  { type: "world", text: "WORLD: G20 summit concludes with climate agreement" },
  { type: "world", text: "WORLD: Tech stocks rally as AI sector reports strong earnings" },
  { type: "world", text: "WORLD: World Cup draws record television viewership" },
  { type: "world", text: "WORLD: Central banks signal pause on interest rate hikes" },
  { type: "world", text: "WORLD: New high-speed rail line opens connecting major cities" },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getGameMinutesUntilDeparture(scheduledDeparture, gameTime) {
  const scheduled = new Date(scheduledDeparture).getTime();
  const current = new Date(gameTime).getTime();
  return (scheduled - current) / (1000 * 60);
}

function formatTime(isoString) {
  if (!isoString) return "--:--";
  const date = new Date(isoString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

function formatPhase(phase) {
  if (!phase) return "Unknown";
  return phase.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function OpsCenterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState, setWorldState, updateFlight, addDelay, addEvent, togglePause, startNegotiation, cancelFlight, getUnreadCount, hireSpecialist, updateActiveAirportsWeather } = useWorldState();
  const { signOut, user } = useAuth();
  const activeFlights = useActiveFlights();

  // Gate announcements archived - simulation mode only

  const [consequences, setConsequences] = useState([]);
  const [showEndOfDayModal, setShowEndOfDayModal] = useState(false);
  const [pendingDayAdvance, setPendingDayAdvance] = useState(false);
  const [openPanel, setOpenPanel] = useState(null); // null | 'routes' | 'consultants' | 'crew' | 'tech' | 'support' | 'advertising' | 'rebrand' | 'fuel'
  const [commandConsoleOpen, setCommandConsoleOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(!worldState?.onboardingComplete);
  const [showStore, setShowStore] = useState(false);

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback(() => {
    setWorldState(prev => ({ ...prev, onboardingComplete: true }));
    setShowOnboarding(false);
  }, [setWorldState]);

  // Detect end of day trigger from GameLoopEngine
  useEffect(() => {
    debug("[OpsCenter] End of day check:", {
      pendingEndOfDay: worldState?.pendingEndOfDay,
      showEndOfDayModal,
      willShowModal: worldState?.pendingEndOfDay && !showEndOfDayModal,
    });
    if (worldState?.pendingEndOfDay && !showEndOfDayModal) {
      debug("[OpsCenter] Showing end of day modal");
      setShowEndOfDayModal(true);
    }
  }, [worldState?.pendingEndOfDay, showEndOfDayModal]);

  // After negotiation completes, advance to next day
  useEffect(() => {
    if (pendingDayAdvance && !worldState?.negotiation) {
      // Negotiation finished - advance to 6 AM next day
      advanceToNextDay();
      setPendingDayAdvance(false);
    }
  }, [pendingDayAdvance, worldState?.negotiation]);

  // Periodically update weather for active airports (every 5 minutes real time)
  useEffect(() => {
    if (!updateActiveAirportsWeather) return;

    // Initial weather fetch
    updateActiveAirportsWeather();

    // Update every 5 minutes
    const weatherInterval = setInterval(() => {
      updateActiveAirportsWeather();
    }, 5 * 60 * 1000);

    return () => clearInterval(weatherInterval);
  }, [updateActiveAirportsWeather]);

  // Advance to next day at 6:00 AM
  const advanceToNextDay = useCallback(() => {
    setWorldState(prev => {
      const currentTime = new Date(prev.gameTime);
      // Set to 6:00 AM next day
      const nextDay = new Date(currentTime);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      nextDay.setUTCHours(6, 0, 0, 0);

      // Clear completed and cancelled flights from previous day - start fresh
      // Check both phase AND status since some flights use one or the other
      // Keep completed flights for financial history, only clear cancelled
const activeFlights = (prev.flights || []).filter(f =>
  f.phase !== 'cancelled' && f.status !== 'cancelled'
);

      return {
        ...prev,
        flights: activeFlights, // Clean slate for new day
        gameTime: nextDay.toISOString(),
        dayNumber: (prev.dayNumber || 1) + 1,
        pendingEndOfDay: false,
        endOfDayHandled: false, // Reset for next day
        isPaused: false, // Resume game
      };
    });
    setShowEndOfDayModal(false);
  }, [setWorldState]);

  // Handle "Advance to Next Day" button click
const handleAdvanceDay = useCallback(() => {
  setShowEndOfDayModal(false);
  setWorldState(prev => ({ ...prev, pendingEndOfDay: false, endOfDayHandled: true }));
  
  // Simple mode: skip negotiations entirely (players have all slots)
  if (worldState?.gameMode === 'simple') {
    advanceToNextDay();
    return;
  }
  
  // Simulation mode: check for slot negotiations
  const nextAirport = getNextNegotiationAirport(worldState);
  
  if (nextAirport) {
    const hubCode = worldState?.airline?.hub || "KPDX";
    const negData = createNegotiationData(
      nextAirport.code,
      nextAirport.name,
      hubCode,
      90,
      worldState?.relations // Pass relations for AI bid generation
    );
    startNegotiation(negData);
    setPendingDayAdvance(true);
  } else {
    advanceToNextDay();
  }
}, [worldState, startNegotiation, advanceToNextDay]);

  // Build dynamic news feed from multiple sources
  const newsItems = useMemo(() => {
    const items = [];
    const flights = worldState?.flights || [];
    const events = worldState?.events || [];
    const airline = worldState?.airline || {};
    const macro = worldState?.macro || {};
    const gameTime = worldState?.gameTime ? new Date(worldState.gameTime) : new Date();

    // 0. LIVE WEATHER ALERTS (highest priority - from Tomorrow.io real-time data)
    const airportWeather = worldState?.airportWeather || {};
    for (const [code, weather] of Object.entries(airportWeather)) {
      const impact = weather?.impact;
      if (impact?.severity === 'grounded') {
        items.push({
          type: 'critical',
          text: `WEATHER: ${code} GROUND STOP - ${impact.reason}`,
          priority: 15,
        });
      } else if (impact?.severity === 'delay' && impact.delayMinutes >= 30) {
        items.push({
          type: 'warning',
          text: `WEATHER: ${code} DELAY +${impact.delayMinutes}min - ${impact.reason}`,
          priority: 12,
        });
      } else if (impact?.severity === 'advisory') {
        items.push({
          type: 'caution',
          text: `WEATHER: ${code} Advisory - ${impact.reason}`,
          priority: 8,
        });
      }
    }

    // 1. RECENT FLIGHT COMPLETIONS (last 5)
    const completedFlights = flights
      .filter(f => f.phase === "completed" && f.revenue)
      .sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0))
      .slice(0, 5);
    
    for (const f of completedFlights) {
      const profit = (f.revenue || 0) - (f.costs || 0);
      items.push({
        type: profit >= 0 ? "profit" : "loss",
        text: `${f.flightNumber} ${f.origin}-${f.destination} completed: ${profit >= 0 ? '+' : ''}$${(profit/1000).toFixed(1)}K profit`,
      });
    }
    
    // 2. CANCELLATIONS (critical news)
    const cancelledFlights = flights
      .filter(f => f.status === "cancelled" || f.phase === "cancelled")
      .slice(0, 3);
    
    for (const f of cancelledFlights) {
      const cost = f.cancellationCost?.totalCost || 0;
      items.push({
        type: "critical",
        text: `CANCELLED: ${f.flightNumber} - $${(cost/1000).toFixed(0)}K loss | ${f.cancellationCost?.affectedPassengers || 0} passengers affected`,
      });
    }
    
    // 3. DELAYED FLIGHTS (in progress)
    const delayedFlights = flights
      .filter(f => f.totalDelayMinutes > 10 && f.phase !== "completed" && f.phase !== "cancelled")
      .slice(0, 3);
    
    for (const f of delayedFlights) {
      items.push({
        type: "warning",
        text: `DELAY: ${f.flightNumber} running +${f.totalDelayMinutes}min late | Currently: ${formatPhase(f.phase)}`,
      });
    }
    
    // 4. MACRO ENGINE NEWS (if available)
    const macroNews = worldState?.newsFeed || [];
    for (const news of macroNews.slice(0, 5)) {
      items.push({
        type: news.severity || "info",
        text: news.headline || news.message || news.text,
      });
    }
    // Weather alerts already added at start of function (section 0)

    // 5. FUEL PRICE INDICATOR
    const fuelMultiplier = macro?.jetFuelPriceMultiplier || 1.0;
    if (fuelMultiplier > 1.15) {
      items.push({
        type: "warning",
        text: `FUEL: JET-A +${Math.round((fuelMultiplier - 1) * 100)}% above baseline - monitor route costs`,
      });
    } else if (fuelMultiplier < 0.9) {
      items.push({
        type: "good",
        text: `FUEL: JET-A -${Math.round((1 - fuelMultiplier) * 100)}% below baseline - favorable operating conditions`,
      });
    }
    
    // 6. DAILY P&L SUMMARY
    const todayCompleted = completedFlights.length;
    const todayRevenue = completedFlights.reduce((sum, f) => sum + (f.revenue || 0), 0);
    const todayCosts = completedFlights.reduce((sum, f) => sum + (f.costs || 0), 0);
    const todayProfit = todayRevenue - todayCosts;
    
    if (todayCompleted > 0) {
      items.push({
        type: todayProfit >= 0 ? "profit" : "loss",
        text: `TODAY: ${todayCompleted} flights | Revenue $${(todayRevenue/1000).toFixed(0)}K | Net ${todayProfit >= 0 ? '+' : ''}$${(todayProfit/1000).toFixed(0)}K`,
      });
    }
    
    // 7. CASH POSITION
    const cash = airline.cash || 0;
    if (cash < 5000000) {
      items.push({
        type: "critical",
        text: `CASH CRITICAL: $${(cash/1000000).toFixed(2)}M remaining - manage costs carefully`,
      });
    }
    
    // 8. REPUTATION (0-15000+ scale, NOT 0-100)
    const repScore = worldState?.reputation?.currentScore || 0;
    const repTier = getReputationTier(repScore);
    if (repScore < 1000) {
      items.push({
        type: "critical",
        text: `REPUTATION LOW: ${repScore.toLocaleString()} (Tier ${repTier}) - build operations to grow`,
      });
    } else if (repScore >= 15000) {
      items.push({
        type: "good",
        text: `ELITE STATUS: ${repScore.toLocaleString()} (Tier 5) - premium pricing power unlocked`,
      });
    } else if (repScore >= 7000) {
      items.push({
        type: "good",
        text: `MAJOR CARRIER: ${repScore.toLocaleString()} (Tier ${repTier}) - strong market position`,
      });
    }
    
    // 9. If nothing interesting, add some contextual flavor
    if (items.length < 3) {
      items.push(...FALLBACK_NEWS.slice(0, 5 - items.length));
    }

    return items;
  }, [worldState?.flights, worldState?.events, worldState?.newsFeed, worldState?.airline, worldState?.macro, worldState?.reputation, worldState?.gameTime, worldState?.slots]);

  // Calculate stats
  const stats = useMemo(() => {
    const s = worldState?.stats || {};
    const airline = worldState?.airline || {};
    const reputation = worldState?.reputation || {};
    const completedFlights = s.completedFlights || 0;
    const onTimeFlights = s.onTimeFlights || 0;
    const otp = completedFlights > 0 ? Math.round((onTimeFlights / completedFlights) * 100) : 100;
    // TIER: Always derive from RepScore using getReputationTier - single source of truth
    const repScore = reputation.currentScore || 0;
    const tier = getReputationTier(repScore);

    // Hub capacity (Simple mode only)
    const homeHub = airline.hub || 'KPDX';
    const aircraft = worldState?.aircraft || [];
    const aircraftAtHub = aircraft.filter(a => a.location === homeHub || a.homeBase === homeHub).length;

    return {
      cash: airline.cash || 0,
      reputation: repScore,
      tier,
      completedFlights,
      onTimePercentage: otp,
      totalProfit: s.totalProfit || 0,
      activeFlights: activeFlights.length,
      hubCapacity: { current: aircraftAtHub, max: HUB_CAPACITY, hub: homeHub },
    };
  }, [worldState, activeFlights]);

  // Today's Operations Metrics - real-time operational snapshot
  const todayOps = useMemo(() => {
    const flights = worldState?.flights || [];
    const gameTime = worldState?.gameTime ? new Date(worldState.gameTime) : new Date();
    const todayStr = gameTime.toDateString();
    const fuelPrice = worldState?.fuelPricePerGallon || 3.50;

    // Filter flights by today
    const todayFlights = flights.filter(f => {
      const depDate = new Date(f.scheduledDeparture).toDateString();
      return depDate === todayStr;
    });

    // Completed today
    const completed = todayFlights.filter(f => f.phase === 'completed' || f.status === 'completed');

    // In progress (airborne phases)
    const airbornePhases = ['takeoff', 'climb', 'cruise', 'descent', 'approach', 'landing'];
    const inProgress = todayFlights.filter(f => airbornePhases.includes(f.phase) || f.hasLaunched);

    // Scheduled but not departed
    const scheduled = todayFlights.filter(f =>
      f.phase === 'scheduled' && f.status !== 'cancelled' && !f.hasLaunched
    );

    // Cancelled today
    const cancelled = todayFlights.filter(f => f.status === 'cancelled' || f.phase === 'cancelled');

    // Revenue today (from completed flights)
    const revenue = completed.reduce((sum, f) => sum + (f.revenue || 0), 0);

    // Fuel cost today (from completed + in-progress)
    const flightsWithFuel = [...completed, ...inProgress];
    const totalGallons = flightsWithFuel.reduce((sum, f) => sum + (f.fuelUsedGallons || f.fuelBurned || 0), 0);
    const fuelCost = totalGallons * fuelPrice;

    // Net profit
    const netProfit = revenue - fuelCost;

    // On-time performance (completed flights with delay < 15min)
    const onTime = completed.filter(f => (f.totalDelayMinutes || 0) < 15);
    const otpPercent = completed.length > 0 ? Math.round((onTime.length / completed.length) * 100) : 100;

    // Delayed flights (delay >= 15min, not cancelled)
    const delayed = todayFlights.filter(f =>
      (f.totalDelayMinutes || 0) >= 15 && f.status !== 'cancelled' && f.phase !== 'cancelled'
    );
    const avgDelay = delayed.length > 0
      ? Math.round(delayed.reduce((sum, f) => sum + (f.totalDelayMinutes || 0), 0) / delayed.length)
      : 0;

    // At-risk flights (weather holds, low fuel warnings, or major delays in progress)
    const atRisk = todayFlights.filter(f => {
      if (f.status === 'cancelled' || f.phase === 'cancelled' || f.phase === 'completed') return false;
      return f.weatherHold || f.fuelCritical || f.diverted || (f.totalDelayMinutes || 0) >= 60;
    });

    return {
      completed: completed.length,
      inProgress: inProgress.length,
      scheduled: scheduled.length,
      cancelled: cancelled.length,
      revenue,
      fuelCost,
      totalGallons,
      netProfit,
      onTime: onTime.length,
      otpPercent,
      delayed: delayed.length,
      avgDelay,
      atRisk: atRisk.length,
      totalFlights: todayFlights.length,
    };
  }, [worldState?.flights, worldState?.gameTime, worldState?.fuelPricePerGallon]);

  // Scheduled = flights that haven't started yet, plus today's cancelled flights
  const scheduledFlights = useMemo(() => {
    const gameDate = worldState?.gameTime ? new Date(worldState.gameTime).toDateString() : new Date().toDateString();
    return (worldState?.flights || [])
      .filter(f => {
        // Show scheduled flights
        if (f.phase === "scheduled" && f.status !== "cancelled") return true;
        // Show cancelled flights only if from today
        if (f.status === "cancelled" || f.phase === "cancelled") {
          const flightDate = new Date(f.scheduledDeparture).toDateString();
          return flightDate === gameDate;
        }
        return false;
      })
      .sort((a, b) => {
        // Cancelled flights go to bottom
        const aCancelled = a.status === "cancelled" || a.phase === "cancelled";
        const bCancelled = b.status === "cancelled" || b.phase === "cancelled";
        if (aCancelled && !bCancelled) return 1;
        if (bCancelled && !aCancelled) return -1;
        return new Date(a.scheduledDeparture) - new Date(b.scheduledDeparture);
      });
  }, [worldState?.flights, worldState?.gameTime]);

  // In Flight = airborne flights, launched flights, plus today's completed/cancelled
  const AIRBORNE_PHASES_LIST = ["takeoff", "climb", "cruise", "descent", "approach", "landing"];
  const airborneFlights = useMemo(() => {
    const gameDate = worldState?.gameTime ? new Date(worldState.gameTime).toDateString() : new Date().toDateString();
    return (worldState?.flights || [])
      .filter(f => {
        // Show flights in airborne phases
        const isAirborne = AIRBORNE_PHASES_LIST.includes(f.phase);
        if (isAirborne) return true;

        // Also show flights that have launched (backup check)
        if (f.hasLaunched || f.status === "in_flight") return true;

        // Finished flights only show if from today
        const isFinished = f.phase === "completed" || f.phase === "cancelled" ||
                          f.status === "completed" || f.status === "cancelled";
        if (isFinished) {
          const flightDate = new Date(f.scheduledDeparture).toDateString();
          return flightDate === gameDate;
        }

        return false;
      })
      .sort((a, b) => {
        // Active flights first, finished at bottom
        const aFinished = a.phase === "completed" || a.phase === "cancelled" || a.status === "cancelled";
        const bFinished = b.phase === "completed" || b.phase === "cancelled" || b.status === "cancelled";
        if (aFinished && !bFinished) return 1;
        if (bFinished && !aFinished) return -1;
        return 0;
      });
  }, [worldState?.flights, worldState?.gameTime]);

  // Phases that indicate the flight is airborne (or post-flight)
  const AIRBORNE_PHASES = ["takeoff", "climb", "cruise", "descent", "approach", "landing"];

  // Flights in ground ops: scheduled flights within T-35 window ONLY
  // Once flight.phase becomes "takeoff" or hasLaunched=true, they move to In Flight
  const groundOpsFlights = useMemo(() => {
    const gameTime = worldState?.gameTime;
    return (worldState?.flights || []).filter(f => {
      // Skip cancelled/completed flights
      if (f.status === "cancelled" || f.phase === "cancelled" || f.phase === "completed") {
        return false;
      }
      // CRITICAL: Skip airborne flights - absolute check, no exceptions!
      if (AIRBORNE_PHASES.includes(f.phase)) {
        return false;
      }
      // Skip if already launched (even if phase somehow got reset)
      if (f.hasLaunched || f.status === "in_flight") {
        return false;
      }
      // Only show scheduled flights within T-35 to T+5 window
      // After T+5, flight should either be airborne or there's a problem
      if (f.phase === "scheduled") {
        const minUntil = getGameMinutesUntilDeparture(f.scheduledDeparture, gameTime);
        // Show from T-35 (35 min before) to T+5 (5 min after scheduled departure)
        // After T+5, if flight hasn't launched, it will be picked up by GameLoopEngine
        return minUntil <= 35 && minUntil > -5;
      }
      return false;
    }).sort((a, b) => new Date(a.scheduledDeparture) - new Date(b.scheduledDeparture));
  }, [worldState?.flights, worldState?.gameTime]);

  const recentEvents = (worldState?.events || []).slice(-8).reverse();
  
  // Time display - use local time (game starts at 6 AM local)
  const gameTime = new Date(worldState?.gameTime || Date.now());
  const hours = gameTime.getHours();
  const minutes = gameTime.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const timeStr = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  const dateStr = gameTime.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

  // Add consequence message - auto-dismiss after 3 seconds
  const showConsequence = useCallback((message, severity = "error") => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setConsequences(prev => [...prev, { id, message, severity }]);
    setTimeout(() => {
      setConsequences(prev => prev.filter(c => c.id !== id));
    }, 3000);
  }, []);

  // Dismiss consequence manually
  const dismissConsequence = useCallback((id) => {
    setConsequences(prev => prev.filter(c => c.id !== id));
  }, []);

  // Handle play/pause
  const handleTogglePause = useCallback(() => {
    if (togglePause) {
      togglePause();
    } else {
      setWorldState(prev => ({ ...prev, isPaused: !prev.isPaused }));
    }
  }, [togglePause, setWorldState]);

  // Panel action handlers
  const handleHireConsultant = useCallback((consultantId) => {
    debug('[Consultants] Hiring:', consultantId);
    setWorldState(prev => {
      debug('[Consultants] Cash:', prev.airline.cash, 'State:', prev.consultants);
      const result = hireConsultant(prev.consultants, consultantId, prev.airline.cash);
      debug('[Consultants] Result:', result);
      if (!result.success) {
        showConsequence(result.error, "error");
        return prev;
      }
      return {
        ...prev,
        consultants: result.state,
        airline: { ...prev.airline, cash: prev.airline.cash - result.cost }
      };
    });
  }, [setWorldState, showConsequence]);

  const handleFireConsultant = useCallback((consultantId) => {
    setWorldState(prev => {
      const result = fireConsultant(prev.consultants, consultantId);
      if (!result.success) {
        console.error('[Consultants] Fire failed:', result.error);
        return prev;
      }
      return { ...prev, consultants: result.state };
    });
  }, [setWorldState]);

  const handleHireCrew = useCallback((crewId) => {
    setWorldState(prev => {
      const result = hireCrew(prev.crewMarket, crewId, prev.airline.cash);
      if (result.error) {
        showConsequence(result.error, "error");
        return prev;
      }
      return {
        ...prev,
        crewMarket: result.state,
        crew: [...prev.crew, result.hiredCrew],
        airline: { ...prev.airline, cash: prev.airline.cash - result.cost }
      };
    });
  }, [setWorldState, showConsequence]);

  const handleFireCrew = useCallback((crewId) => {
    setWorldState(prev => {
      const result = fireCrew(prev.crewMarket, crewId);
      return {
        ...prev,
        crewMarket: result,
        crew: prev.crew.filter(c => c.id !== crewId)
      };
    });
  }, [setWorldState]);

  const handlePurchaseTech = useCallback((techId) => {
    setWorldState(prev => {
      // Tier is computed from reputation, not stored
      const computedTier = getReputationTier(prev.reputation?.currentScore || 0);
      const result = purchaseTech(prev.tech, techId, prev.airline.cash, computedTier);
      if (result.error) {
        showConsequence(result.error, "error");
        return prev;
      }
      showConsequence(`Purchased ${techId}!`, "success");
      return {
        ...prev,
        tech: result.state,
        airline: { ...prev.airline, cash: prev.airline.cash - result.cost }
      };
    });
  }, [setWorldState, showConsequence]);

  const handleHireSupportCrew = useCallback((crewId) => {
    setWorldState(prev => {
      const result = hireSupportCrew(prev.supportCrew, crewId, prev.airline.cash);
      if (result.error) {
        showConsequence(result.error, "error");
        return prev;
      }
      return {
        ...prev,
        supportCrew: result.state,
        airline: { ...prev.airline, cash: prev.airline.cash - result.cost }
      };
    });
  }, [setWorldState, showConsequence]);

  const handleFireSupportCrew = useCallback((crewId) => {
    setWorldState(prev => {
      const result = fireSupportCrew(prev.supportCrew, crewId);
      return { ...prev, supportCrew: result };
    });
  }, [setWorldState]);

  const handleLaunchCampaign = useCallback((campaignType, target) => {
    debug('[Advertising] Launching campaign:', { campaignType, target });
    setWorldState(prev => {
      const result = launchCampaign(prev.advertising, campaignType, target, prev.airline.cash);
      debug('[Advertising] Result:', result);
      if (!result.success) {
        showConsequence(result.error, "error");
        return prev;
      }
      showConsequence(`Launched ${campaignType} campaign!`, "success");
      return {
        ...prev,
        advertising: result.state,
        airline: { ...prev.airline, cash: prev.airline.cash - result.cost }
      };
    });
  }, [setWorldState, showConsequence]);

  const handleCancelCampaign = useCallback((campaignId) => {
    debug('[Advertising] Cancelling campaign:', campaignId);
    setWorldState(prev => {
      const result = cancelCampaign(prev.advertising, campaignId);
      if (!result.success) {
        showConsequence(result.error, "error");
        return prev;
      }
      return { ...prev, advertising: result.state };
    });
  }, [setWorldState, showConsequence]);

  const handleRebrand = useCallback(() => {
    debug('[Rebranding] Executing rebrand');
    setWorldState(prev => {
      const result = executeRebrand(prev.rebranding, prev.relations, prev.airline, prev.dayNumber);
      debug('[Rebranding] Result:', result);
      if (!result.success) {
        showConsequence(result.error || 'Rebrand failed', "error");
        return prev;
      }
      showConsequence('Rebrand complete! Design your new identity.', "success");
      return {
        ...prev,
        rebranding: result.rebrandState,
        relations: result.relationsState,
        airline: { ...prev.airline, cash: prev.airline.cash - result.cost }
      };
    });
  }, [setWorldState, showConsequence]);

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: "#0a0a0a", color: "#e5e5e5" }}>
      {/* Negotiation Modals */}
      <NegotiationController />

      {/* Tutorial System */}
      <TutorialController />

      {/* Fleet Unlock Notification (Simulation mode) */}
      <FleetUnlockModal />

      {/* Onboarding Flow - shows game world behind at 85% opacity */}
      {showOnboarding && (
        <OnboardingFlow
          gameMode={worldState?.gameMode}
          onComplete={handleOnboardingComplete}
        />
      )}

      {/* Panel Modals */}
      {openPanel === 'routes' && (
        <RouteNetworkPanel onClose={() => setOpenPanel(null)} />
      )}
      {openPanel === 'consultants' && (
        <ConsultantsPanel
          consultantState={worldState?.consultants}
          cash={worldState?.airline?.cash || 0}
          tier={stats.tier}
          onHire={handleHireConsultant}
          onFire={handleFireConsultant}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'crew' && (
        <CrewMarketPanel
          crewState={worldState?.crewMarket}
          cash={worldState?.airline?.cash || 0}
          onHire={handleHireCrew}
          onFire={handleFireCrew}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'tech' && (
        <TechSystemsPanel
          techState={worldState?.tech}
          cash={worldState?.airline?.cash || 0}
          tier={stats.tier}
          onPurchase={handlePurchaseTech}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'support' && (
        <SupportCrewPanel
          supportState={worldState?.supportCrew}
          cash={worldState?.airline?.cash || 0}
          onHire={handleHireSupportCrew}
          onFire={handleFireSupportCrew}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'advertising' && (
        <AdvertisingPanel
          advertisingState={worldState?.advertising}
          cash={worldState?.airline?.cash || 0}
          slots={worldState?.slots || {}}
          onLaunch={handleLaunchCampaign}
          onCancel={handleCancelCampaign}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'rebrand' && (
        <RebrandingPanel
          rebrandingState={worldState?.rebranding}
          relationsState={worldState?.relations}
          cash={worldState?.airline?.cash || 0}
          gameDay={worldState?.dayNumber || 1}
          airline={worldState?.airline}
          onRebrand={handleRebrand}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'innerCircle' && (
        <InnerCirclePanel
          innerCircleState={worldState?.innerCircle}
          cash={worldState?.airline?.cash || 0}
          onHire={(specialist) => {
            const success = hireSpecialist(specialist);
            if (success) {
              showConsequence(`Hired ${specialist.name} (${specialist.title})`, "success");
            } else {
              showConsequence(`Cannot hire ${specialist.name} - insufficient funds or already hired`, "error");
            }
          }}
          onClose={() => setOpenPanel(null)}
        />
      )}
      {openPanel === 'fuel' && worldState?.gameMode === 'simulation' && (
        <FuelPanel onClose={() => setOpenPanel(null)} />
      )}

      {/* Command Console - Hamburger Menu */}
      <CommandConsole
        isOpen={commandConsoleOpen}
        onClose={() => setCommandConsoleOpen(false)}
        onOpenPanel={setOpenPanel}
      />

      {/* End of Day Modal */}
      <Dialog open={showEndOfDayModal} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-md"
          style={{
            backgroundColor: '#0a0a0a',
            borderColor: '#60ffd0',
            borderWidth: '2px',
            color: '#e5e5e5',
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl text-center" style={{ color: '#60ffd0' }}>
              {t('ops.dayComplete', { day: worldState?.dayNumber || 1 })}
            </DialogTitle>
          </DialogHeader>

          <div className="py-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#0d2a20' }}>
              <span style={{ color: '#60ffd0', fontSize: '2rem', fontWeight: 'bold' }}>OK</span>
            </div>

            <p className="text-lg mb-2" style={{ color: '#a3a3a3' }}>
              {t('ops.allOpsFinished')}
            </p>

            {/* Negotiation hint removed - negotiations are now relations-based, not Day 1 hardcoded */}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              onClick={() => navigate('/financials')}
              style={{ backgroundColor: '#333', color: '#e5e5e5' }}
            >
              {t('ops.viewSummary')}
            </Button>
            <Button
              onClick={handleAdvanceDay}
              style={{ backgroundColor: '#60ffd0', color: '#000' }}
            >
              {/* Only show "Begin Negotiations" for Simulation mode Day 1 */}
              {(worldState?.dayNumber || 1) === 1 && worldState?.gameMode !== 'simple'
                ? t('ops.beginNegotiations')
                : t('ops.advanceToDay', { day: (worldState?.dayNumber || 1) + 1 })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dev Cheat Console moved to Layout.jsx - press ` anywhere to toggle */}

      {/* Consequence Toasts - click to dismiss */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {consequences.map(c => (
          <div
            key={c.id}
            onClick={() => dismissConsequence(c.id)}
            className="p-4 rounded-lg shadow-lg max-w-md cursor-pointer consequence-toast"
            style={{
              backgroundColor: c.severity === "error" ? "#7f1d1d" : c.severity === "success" ? "#14532d" : "#7f6d1d",
              border: `2px solid ${c.severity === "error" ? "#ef4444" : c.severity === "success" ? "#22c55e" : "#eab308"}`
            }}>
            <div className="flex items-center gap-2">
              <span className="flex-shrink-0 font-bold" style={{ color: c.severity === "error" ? "#ef4444" : c.severity === "success" ? "#22c55e" : "#eab308" }}>!</span>
              <span className="font-medium" style={{ color: "#fff" }}>{c.message}</span>
              <span className="text-xs ml-2 opacity-50" style={{ color: "#fff" }}>X</span>
            </div>
          </div>
        ))}
      </div>

      {/* Header - Redesigned: Clock absolutely centered, stats spread, nav on left sidebar */}
      <div className="flex items-center px-4 py-2 border-b relative" style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
        {/* Left - Title & Hamburger */}
        <div className="flex items-center gap-3" style={{ minWidth: "180px" }}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setCommandConsoleOpen(true)}
            style={{
              display: "none", // Hidden for Simple mode launch - keep code for later
              backgroundColor: "transparent",
              border: "1px solid #333",
              color: "#60ffd0",
              padding: "8px",
            }}
            title="Command Console"
          >
            |||
          </Button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: "#60ffd0" }}>{t('ops.title')}</h1>
            <p className="text-xs" style={{ color: "#525252" }}>{worldState?.airline?.name || "New Airways"}</p>
          </div>
        </div>

        {/* Stats - Left side */}
        <div className="flex items-center gap-4 ml-4">
          <StatBadgeLarge value={`$${(stats.cash / 1000000).toFixed(1)}M`} label={t('ops.cash')} color="#22c55e" />
          <div className="px-3 py-2 rounded" style={{ backgroundColor: "#111", border: "1px solid #1a1a1a" }}>
            <RevenueTickerCompact />
          </div>
          {/* Fuel Price Ticker - Simulation Mode Only */}
          {worldState?.gameMode === 'simulation' && (
            <FuelPriceTicker
              fuelState={worldState?.fuel}
              chaos={worldState?.fuelMarketChaos}
              onClick={() => setOpenPanel('fuel')}
            />
          )}
          <StatBadgeLarge value={stats.reputation.toLocaleString()} label={`${t('ops.tier')} ${stats.tier}`} color="#60ffd0" />
          <StatBadgeLarge value={`${stats.onTimePercentage}%`} label={t('ops.otp')} color={stats.onTimePercentage >= 85 ? "#22c55e" : "#eab308"} />
          {/* Hub Capacity (Simple mode) */}
          {(worldState?.gameMode === 'simple' || worldState?.gameMode === 'arcade') && (
            <StatBadgeLarge
              value={`${stats.hubCapacity.current}/${stats.hubCapacity.max}`}
              label={stats.hubCapacity.hub}
              color={stats.hubCapacity.current >= stats.hubCapacity.max ? "#ef4444" : stats.hubCapacity.current >= stats.hubCapacity.max - 5 ? "#f59e0b" : "#60ffd0"}
            />
          )}
        </div>

        {/* Speed Controls - Left of center */}
        <div className="flex gap-1 ml-6">
          <Button size="sm"
            onClick={handleTogglePause}
            className="w-10 h-8 text-xs font-bold"
            style={worldState?.isPaused
              ? { backgroundColor: "#22c55e", color: "#000", padding: 0 }
              : { backgroundColor: "#ef4444", color: "#fff", padding: 0 }}>
            {worldState?.isPaused ? "GO" : "||"}
          </Button>
          {/* Speed options based on permanent unlocks: 1x,2x always | 4x at Day 4 | 12x at EOS */}
          {(() => {
            const unlocks = worldState?.unlocks || {};
            const speeds = [1, 2];
            speeds.push(4); // 4x always available from start
            if (unlocks.speed12x) speeds.push(12); // 12x unlocked with EOS
            return speeds.map((speed) => (
              <Button key={speed} size="sm"
                className="w-8 h-8 p-0 text-xs"
                variant={worldState?.gameSpeed === speed && !worldState?.isPaused ? "default" : "outline"}
                onClick={() => setWorldState(prev => ({ ...prev, gameSpeed: speed, isPaused: false }))}
                style={worldState?.gameSpeed === speed && !worldState?.isPaused
                  ? { backgroundColor: "#60ffd0", color: "#000" }
                  : { borderColor: "#333", color: "#525252" }}>
                {speed}x
              </Button>
            ));
          })()}
          {canManuallyEndDay(worldState) && !worldState?.isPaused && (
            <Button
              size="sm"
              className="h-8 px-2 text-xs ml-1"
              onClick={() => setWorldState(prev => ({ ...prev, pendingEndOfDay: true, isPaused: true }))}
              style={{ backgroundColor: '#525252', color: '#e5e5e5', border: '1px solid #333' }}
              title="End day early (no active flights)"
            >
              {t('ops.endDay')}
            </Button>
          )}
        </div>

        {/* CENTER - Retro LED Clock (absolutely centered) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="px-4 py-2 rounded" style={{
            backgroundColor: "#0a0a0a",
            border: "2px solid #1a1a1a",
            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.8)"
          }}>
            <div style={{
              fontFamily: "'Segment7', 'DSEG7 Classic', 'Courier New', monospace",
              fontSize: "1.75rem",
              fontWeight: "bold",
              color: "#ff2020",
              textShadow: "0 0 10px #ff2020, 0 0 20px #ff2020",
              letterSpacing: "2px",
              lineHeight: 1,
            }}>
              {timeStr}
            </div>
            <div className="text-xs text-center mt-1" style={{ color: "#ff2020", opacity: 0.7 }}>
              {dateStr} | {t('opsPanel.day')} {worldState?.dayNumber || 1}
            </div>
          </div>
        </div>

        {/* Spacer to push right content */}
        <div className="flex-1" />

        {/* Stats - Right side */}
        <div className="flex items-center gap-4 mr-4">
          <StatBadgeLarge value={stats.activeFlights} label={t('ops.active')} color="#3b82f6" />
          <StatBadgeLarge value={stats.completedFlights} label={t('ops.done')} color="#22c55e" />
          <EOSStatusBadge worldState={worldState} tier={stats.tier} />
        </div>

        {/* Right - Radio, Email, Knowledge Base, Logout */}
        <div className="flex items-center gap-3">
          {/* Radio Toggle */}
          <RadioToggle />

          {/* Email Button - Fills red when unread */}
          <Button
            size="sm"
            onClick={() => navigate("/Inbox")}
            className="relative"
            style={{
              backgroundColor: getUnreadCount() > 0 ? "#ef4444" : "#1e3a8a",
              border: "none",
              color: "#fff",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: "600",
            }}
          >
            {t('ops.email')}
            {getUnreadCount() > 0 && (
              <span
                className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{ backgroundColor: "#fff", color: "#ef4444" }}
              >
                {getUnreadCount() > 9 ? "9+" : getUnreadCount()}
              </span>
            )}
          </Button>

          {/* Knowledge Base */}
          <Button
            onClick={() => navigate("/knowledge-base")}
            style={{
              backgroundColor: "#8B4513",
              color: "#fff",
              border: "2px solid #a0522d",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "600",
              padding: "8px 16px",
            }}
          >
            {t('ops.knowledgeBase')}
          </Button>

          {/* Exit Game Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate('/MainMenu')}
            style={{ backgroundColor: "transparent", border: "1px solid #333", color: "#525252", padding: "8px 12px", fontSize: "11px" }}
            title="Exit Game"
          >
            {t('ops.out')}
          </Button>
        </div>
      </div>

      {/* Empty Fleet Warning */}
      {(!worldState?.aircraft || worldState.aircraft.length === 0) && (
        <div className="mx-4 mt-4 p-6 rounded-lg border-2 border-dashed" style={{ backgroundColor: "#1a1a0a", borderColor: "#f59e0b" }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ color: "#f59e0b" }}>
                {t('ops.noAircraft')}
              </h2>
              <p style={{ color: "#a3a3a3" }}>
                <span style={{ color: "#22c55e", fontWeight: "bold" }}>${((worldState?.airline?.cash || 0) / 1000000).toFixed(1)}M</span> - {t('ops.noAircraftDesc')}
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => navigate("/AircraftMarket")}
              style={{ backgroundColor: "#f59e0b", color: "#000", fontSize: "1.1rem", padding: "1rem 2rem" }}
            >
              {t('ops.browseMarket')}
            </Button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          MAIN CONTENT - 50/50 TWO-COLUMN SPLIT
          Left: DEPARTURES | Right: OPERATIONS
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex gap-2 p-2 overflow-hidden">

        {/* ═══════════════════════════════════════════════════════════════════
            LEFT COLUMN - DEPARTURES (50%)
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="w-1/2 flex flex-col overflow-hidden">
          {/* Action buttons */}
          <div className="flex items-center gap-2 mb-2 px-2">
            <Button size="sm" onClick={() => navigate("/routes")} style={{ backgroundColor: "#60ffd0", color: "#000" }}>
              {t('ops.newFlight')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/WorldMap")} style={{ borderColor: "#333", color: "#737373" }}>
              {t('ops.viewMap')}
            </Button>
          </div>

          {/* Departures Board - Full Height */}
          <div className="flex-1 overflow-hidden">
            <SplitFlapDisplay
              flights={[...scheduledFlights, ...airborneFlights]}
              airportWeather={worldState?.airportWeather || {}}
              maxRows={30}
              title={t('ops.departures')}
            />
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            RIGHT COLUMN - OPERATIONS (50%) - 1970s NASA CONTROL ROOM
            ═══════════════════════════════════════════════════════════════════ */}
        <div
          className="w-1/2 rounded overflow-hidden flex flex-col ops-panel-retro"
          style={{
            backgroundColor: '#0A0A0A',
            border: '4px solid #00AAFF',
            boxShadow: '0 0 20px rgba(0, 170, 255, 0.4), inset 0 0 30px rgba(0, 255, 255, 0.05)',
            fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
            position: 'relative',
          }}
        >
          {/* Scan lines overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 255, 0.03) 2px, rgba(0, 255, 255, 0.03) 4px)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ position: 'relative', zIndex: 1 }}>

            {/* ═══════════════════════════════════════════════════════════════
                CRITICAL ALERTS - Top Priority
                ═══════════════════════════════════════════════════════════════ */}
            {(() => {
              const weatherHolds = [...scheduledFlights, ...airborneFlights].filter(f => f.weatherHold);
              const diversions = [...scheduledFlights, ...airborneFlights].filter(f => f.diverted && f.status !== 'completed');
              const lowFuel = airborneFlights.filter(f => f.fuelRemaining && (f.fuelRemaining / (f.fuelLoaded || 1)) < 0.25);
              const majorDelays = [...scheduledFlights, ...airborneFlights].filter(f => (f.totalDelayMinutes || 0) >= 60 && !f.weatherHold);
              const hasAlerts = weatherHolds.length > 0 || diversions.length > 0 || lowFuel.length > 0 || majorDelays.length > 0;

              return hasAlerts ? (
                <div
                  className="rounded"
                  style={{
                    backgroundColor: '#330000',
                    border: '3px solid #FF0000',
                    boxShadow: '0 0 15px rgba(255, 0, 0, 0.6), inset 0 0 20px rgba(255, 0, 0, 0.1)',
                  }}
                >
                  <div className="px-3 py-2 text-center" style={{ borderBottom: '2px solid #FF0000' }}>
                    <span style={{
                      color: '#FF0000',
                      fontSize: '13px',
                      letterSpacing: '4px',
                      fontWeight: '700',
                      textShadow: '0 0 10px rgba(255, 0, 0, 0.8)',
                    }}>
                      {t('opsPanel.criticalAlerts')}
                    </span>
                  </div>
                  <div className="p-2 space-y-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {lowFuel.map(f => {
                      const pct = Math.round((f.fuelRemaining / (f.fuelLoaded || 1)) * 100);
                      const minsRemaining = Math.round((f.fuelRemaining || 0) / (f.fuelBurnRate || 100));
                      return (
                        <div key={`fuel-${f.id}`} className="px-2 py-1" style={{ color: '#FF0000', fontSize: '12px', textShadow: '0 0 5px rgba(255, 0, 0, 0.5)' }}>
                          {f.flightNumber} - {t('opsPanel.fuelCritical')} - {minsRemaining} {t('opsPanel.minToEmpty')}
                        </div>
                      );
                    })}
                    {weatherHolds.map(f => (
                      <div key={`wx-${f.id}`} className="px-2 py-1" style={{ color: '#FFA500', fontSize: '12px', textShadow: '0 0 5px rgba(255, 165, 0, 0.5)' }}>
                        {f.destination} - {t('opsPanel.weatherIfr')} - {f.flightNumber} {t('opsPanel.holding')}
                      </div>
                    ))}
                    {diversions.map(f => (
                      <div key={`div-${f.id}`} className="px-2 py-1" style={{ color: '#FF0000', fontSize: '12px', textShadow: '0 0 5px rgba(255, 0, 0, 0.5)' }}>
                        {f.flightNumber} - {t('opsPanel.divertedTo')} {f.divertedTo || 'ALT'}
                      </div>
                    ))}
                    {majorDelays.slice(0, 3).map(f => (
                      <div key={`dly-${f.id}`} className="px-2 py-1" style={{ color: '#FFA500', fontSize: '12px', textShadow: '0 0 5px rgba(255, 165, 0, 0.5)' }}>
                        {f.flightNumber} - {t('opsPanel.delayed')} +{f.totalDelayMinutes}M
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className="rounded p-3 text-center"
                  style={{
                    backgroundColor: '#002200',
                    border: '3px solid #00FF00',
                    boxShadow: '0 0 10px rgba(0, 255, 0, 0.4)',
                  }}
                >
                  <span style={{
                    color: '#00FF00',
                    fontSize: '12px',
                    letterSpacing: '3px',
                    textShadow: '0 0 8px rgba(0, 255, 0, 0.8)',
                  }}>
                    {t('opsPanel.allSystemsNominal')}
                  </span>
                </div>
              );
            })()}

            {/* ═══════════════════════════════════════════════════════════════
                FLEET STATUS
                ═══════════════════════════════════════════════════════════════ */}
            <div
              className="rounded"
              style={{
                backgroundColor: '#1A1A1A',
                border: '3px solid #00AAFF',
                boxShadow: '0 0 10px rgba(0, 170, 255, 0.3), inset 0 0 15px rgba(0, 255, 255, 0.05)',
              }}
            >
              <div className="px-3 py-2 text-center" style={{ borderBottom: '2px solid #00AAFF' }}>
                <span style={{
                  color: '#00FFFF',
                  fontSize: '12px',
                  letterSpacing: '3px',
                  fontWeight: '700',
                  textShadow: '0 0 8px rgba(0, 255, 255, 0.6)',
                }}>
                  {t('opsPanel.fleetStatus')} - {t('opsPanel.day')} {worldState?.dayNumber || 1}, {timeStr}
                </span>
              </div>
              <div className="p-3" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px' }}>
                {(() => {
                  const totalAircraft = (worldState?.aircraft || []).length;
                  // Count only flights actually in the air (not completed/cancelled)
                  const actuallyFlying = airborneFlights.filter(f =>
                    AIRBORNE_PHASES.includes(f.phase) || (f.status === "in_flight" && f.phase !== "completed" && f.phase !== "cancelled")
                  ).length;
                  const onGround = Math.max(0, totalAircraft - actuallyFlying);
                  return (
                    <>
                      <div style={{ color: '#E0E0E0', marginBottom: '4px' }}>
                        {t('opsPanel.totalAircraft')} <span style={{ color: '#00FFFF', fontWeight: '700' }}>{totalAircraft}</span>
                      </div>
                      <div style={{ color: '#E0E0E0' }}>
                        {t('opsPanel.flyingNow')} <span style={{ color: '#00FF00', fontWeight: '700' }}>{actuallyFlying}</span>
                        <span style={{ color: '#555', margin: '0 12px' }}>|</span>
                        {t('opsPanel.onGround')} <span style={{ color: '#FFA500', fontWeight: '700' }}>{onGround}</span>
                      </div>
                    </>
                  );
                })()}
                <div style={{ color: '#E0E0E0', marginTop: '4px' }}>
                  {t('opsPanel.flightsToday')} <span style={{ color: '#00FF00', fontWeight: '700' }}>{todayOps.completed}</span> {t('opsPanel.completed')}
                  <span style={{ color: '#555', margin: '0 8px' }}>|</span>
                  <span style={{ color: '#00FFFF', fontWeight: '700' }}>{todayOps.inProgress}</span> {t('opsPanel.active')}
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                EOS REQUIREMENTS - All 7 shown together
                ═══════════════════════════════════════════════════════════════ */}
            {(() => {
              const eos = worldState?.eos || {};
              const aircraft = worldState?.aircraft || [];
              const tier = stats.tier || 1;
              const hasTier5 = tier >= 5;
              const hasFleet30 = aircraft.length >= 30;
              const streakDays = eos.consecutiveDays || 0;
              const hasEOS = eos.hasEOS || false;

              // Daily metrics
              const dailyCashflow = (worldState?.stats?.dailyRevenue || 0) - (worldState?.stats?.dailyCosts || 0);
              const dailyCancellations = worldState?.stats?.dailyCancellations || 0;
              const dailySafetyIncidents = 0; // Track from state when available
              const dailyDepartures = worldState?.stats?.dailyDepartures || 0;
              const otpMet = stats.onTimePercentage >= 85;
              const cancelMet = dailyCancellations === 0;
              const safetyMet = dailySafetyIncidents === 0;
              const cashflowMet = dailyCashflow >= 0;
              const flightsMet = dailyDepartures >= 20;

              // Count how many of the 7 requirements are met
              const metCount = [hasTier5, hasFleet30, otpMet, cancelMet, safetyMet, cashflowMet, flightsMet].filter(Boolean).length;

              return (
                <div
                  className="rounded"
                  style={{
                    backgroundColor: '#1A1A1A',
                    border: `3px solid ${hasEOS ? '#FFD700' : '#00AAFF'}`,
                    boxShadow: hasEOS ? '0 0 20px rgba(255, 215, 0, 0.5)' : '0 0 10px rgba(0, 170, 255, 0.3)',
                  }}
                >
                  <div className="px-3 py-2 text-center" style={{ borderBottom: `2px solid ${hasEOS ? '#FFD700' : '#00AAFF'}` }}>
                    <span style={{
                      color: hasEOS ? '#FFD700' : '#00FFFF',
                      fontSize: '12px',
                      letterSpacing: '3px',
                      fontWeight: '700',
                      textShadow: hasEOS ? '0 0 10px rgba(255, 215, 0, 0.8)' : '0 0 8px rgba(0, 255, 255, 0.6)',
                    }}>
                      {t('opsPanel.eosProgress')} {hasEOS ? t('opsPanel.eliteStatusActive') : `${streakDays}/7 ${t('opsPanel.consecutiveDays')}`}
                    </span>
                  </div>
                  <div className="p-3" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px' }}>
                    {/* Gates Section */}
                    <div style={{ color: '#888', marginBottom: '6px', letterSpacing: '1px', fontSize: '10px' }}>
                      {t('opsPanel.gates')}
                    </div>
                    <div style={{ marginLeft: '8px', marginBottom: '10px' }}>
                      <div style={{ color: hasTier5 ? '#00FF00' : '#888', marginBottom: '2px' }}>
                        <span style={{ color: hasTier5 ? '#00FF00' : '#555' }}>{hasTier5 ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.tier5Rep', { tier })}</span>
                      </div>
                      <div style={{ color: hasFleet30 ? '#00FF00' : '#888' }}>
                        <span style={{ color: hasFleet30 ? '#00FF00' : '#555' }}>{hasFleet30 ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.aircraft30', { count: aircraft.length })}</span>
                      </div>
                    </div>

                    {/* Daily Streak Section */}
                    <div style={{ color: '#888', marginBottom: '6px', letterSpacing: '1px', fontSize: '10px' }}>
                      {t('opsPanel.dailyStreak')}
                    </div>
                    <div style={{ marginLeft: '8px', marginBottom: '10px' }}>
                      <div style={{ color: otpMet ? '#00FF00' : '#888', marginBottom: '2px' }}>
                        <span style={{ color: otpMet ? '#00FF00' : '#555' }}>{otpMet ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.otp85', { percent: stats.onTimePercentage })}</span>
                      </div>
                      <div style={{ color: cancelMet ? '#00FF00' : '#FF0000', marginBottom: '2px' }}>
                        <span style={{ color: cancelMet ? '#00FF00' : '#FF0000' }}>{cancelMet ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.zeroCancellations', { count: dailyCancellations })}</span>
                      </div>
                      <div style={{ color: safetyMet ? '#00FF00' : '#FF0000', marginBottom: '2px' }}>
                        <span style={{ color: safetyMet ? '#00FF00' : '#FF0000' }}>{safetyMet ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.zeroSafety', { count: dailySafetyIncidents })}</span>
                      </div>
                      <div style={{ color: cashflowMet ? '#00FF00' : '#FF0000', marginBottom: '2px' }}>
                        <span style={{ color: cashflowMet ? '#00FF00' : '#FF0000' }}>{cashflowMet ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.positiveCashFlow', { amount: `${dailyCashflow >= 0 ? '+' : ''}$${(dailyCashflow / 1000).toFixed(0)}K` })}</span>
                      </div>
                      <div style={{ color: flightsMet ? '#00FF00' : '#FF0000' }}>
                        <span style={{ color: flightsMet ? '#00FF00' : '#FF0000' }}>{flightsMet ? '☑' : '☐'}</span>
                        <span style={{ marginLeft: '8px' }}>{t('opsPanel.flights20', { count: dailyDepartures })}</span>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })()}

            {/* ═══════════════════════════════════════════════════════════════
                TODAY'S FINANCIALS
                ═══════════════════════════════════════════════════════════════ */}
            <div
              className="rounded"
              style={{
                backgroundColor: '#1A1A1A',
                border: '3px solid #00AAFF',
                boxShadow: '0 0 10px rgba(0, 170, 255, 0.3), inset 0 0 15px rgba(0, 255, 255, 0.05)',
              }}
            >
              <div className="px-3 py-2 text-center" style={{ borderBottom: '2px solid #00AAFF' }}>
                <span style={{
                  color: '#00FFFF',
                  fontSize: '12px',
                  letterSpacing: '3px',
                  fontWeight: '700',
                  textShadow: '0 0 8px rgba(0, 255, 255, 0.6)',
                }}>
                  {t('opsPanel.todaysFinancials')}
                </span>
              </div>
              <div className="p-3" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px' }}>
                {(() => {
                  const fuelPrice = worldState?.fuelPricePerGallon || worldState?.fuel?.currentPrice || 3.50;
                  const gallonsUsed = todayOps.totalGallons || 0;
                  const fuelCost = gallonsUsed * fuelPrice;
                  const netProfit = todayOps.revenue - fuelCost;

                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#888' }}>{t('opsPanel.revenue')}</span>
                        <span style={{ color: '#00FF00', fontWeight: '700', textShadow: '0 0 5px rgba(0, 255, 0, 0.5)' }}>
                          ${todayOps.revenue.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#888' }}>{t('opsPanel.fuelCost')}</span>
                        <span style={{ color: '#FF0000', fontWeight: '700', textShadow: '0 0 5px rgba(255, 0, 0, 0.4)' }}>
                          -${Math.round(fuelCost).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#888' }}>{t('opsPanel.fuelUsed')}</span>
                        <span style={{ color: '#FFA500' }}>
                          {Math.round(gallonsUsed).toLocaleString()} {t('opsPanel.gal')} @ ${fuelPrice.toFixed(2)}/{t('opsPanel.gal')}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #333' }}>
                        <span style={{ color: '#E0E0E0', fontWeight: '700' }}>{t('opsPanel.netProfit')}</span>
                        <span style={{
                          color: netProfit >= 0 ? '#00FF00' : '#FF0000',
                          fontWeight: '700',
                          fontSize: '15px',
                          textShadow: `0 0 8px ${netProfit >= 0 ? 'rgba(0, 255, 0, 0.6)' : 'rgba(255, 0, 0, 0.6)'}`,
                        }}>
                          {netProfit >= 0 ? '+' : ''}${Math.round(netProfit).toLocaleString()}
                        </span>
                      </div>
                      {/* Dynamic pricing info */}
                      <div style={{
                        marginTop: '12px',
                        padding: '8px',
                        backgroundColor: 'rgba(96, 255, 208, 0.1)',
                        border: '1px solid rgba(96, 255, 208, 0.3)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}>
                        <span style={{ color: '#60ffd0', fontSize: '14px' }}>💡</span>
                        <span style={{
                          color: 'rgba(96, 255, 208, 0.8)',
                          fontSize: '11px',
                          lineHeight: '1.4'
                        }}>
                          {t('opsPanel.dynamicPricing')}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                RECENT COMPLETIONS (RECEIPTS)
                ═══════════════════════════════════════════════════════════════ */}
            <div
              className="rounded"
              style={{
                backgroundColor: '#1A1A1A',
                border: '3px solid #00AAFF',
                boxShadow: '0 0 10px rgba(0, 170, 255, 0.3), inset 0 0 15px rgba(0, 255, 255, 0.05)',
              }}
            >
              <div className="px-3 py-2 text-center" style={{ borderBottom: '2px solid #00AAFF' }}>
                <span style={{
                  color: '#00FFFF',
                  fontSize: '12px',
                  letterSpacing: '3px',
                  fontWeight: '700',
                  textShadow: '0 0 8px rgba(0, 255, 255, 0.6)',
                }}>
                  {t('opsPanel.recentCompletions')}
                </span>
              </div>
              <div className="p-2" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px' }}>
                {(() => {
                  const completedFlights = (worldState?.flights || [])
                    .filter(f => f.phase === 'completed' || f.status === 'completed')
                    .slice(-5)
                    .reverse();

                  return completedFlights.length === 0 ? (
                    <div style={{ color: '#555', padding: '12px', textAlign: 'center' }}>
                      {t('opsPanel.noCompletedToday')}
                    </div>
                  ) : (
                    completedFlights.map(f => {
                      const wasOnTime = (f.totalDelayMinutes || 0) <= 15;
                      const landedTime = f.actualArrival ? new Date(f.actualArrival).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--';
                      const profit = f.revenue || Math.round(Math.random() * 20000 + 10000); // Fallback for display

                      return (
                        <div
                          key={f.id}
                          className="px-2 py-1"
                          style={{ borderBottom: '1px solid #222' }}
                        >
                          <span style={{ color: wasOnTime ? '#00FF00' : '#FFA500' }}>✓</span>
                          <span style={{ color: '#00FFFF', marginLeft: '8px', fontWeight: '600' }}>{f.flightNumber}</span>
                          <span style={{ color: '#888', marginLeft: '8px' }}>{f.origin}→{f.destination}</span>
                          <span style={{ color: '#E0E0E0', marginLeft: '12px' }}>{landedTime}</span>
                          <span style={{ color: wasOnTime ? '#00FF00' : '#FFA500', marginLeft: '8px' }}>
                            {wasOnTime ? t('opsPanel.onTime') : `+${f.totalDelayMinutes}M`}
                          </span>
                          <span style={{ color: '#00FF00', marginLeft: '12px', fontWeight: '700' }}>
                            +${profit.toLocaleString()}
                          </span>
                        </div>
                      );
                    })
                  );
                })()}
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* News Ticker - Scrolling Right to Left */}
      <div className="pr-4 pb-4">
        <div className="rounded overflow-hidden" style={{ backgroundColor: "#1a0a0a", border: "1px solid #3a1a1a" }}>
          <div className="flex items-center">
            <div className="px-3 py-2 flex-shrink-0" style={{ backgroundColor: "#2a0a0a", borderRight: "1px solid #3a1a1a" }}>
              <span style={{ color: "#ef4444", fontWeight: "bold", fontSize: "12px" }}>{t('ops.news')}</span>
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="ticker-wrapper">
                <div className="ticker-content">
                  {/* Duplicate content for seamless loop */}
                  {[...newsItems, ...newsItems].map((item, idx) => {
                    // Color based on news type
                    let color = "#f87171"; // default red
                    if (item.type === "profit" || item.type === "good") color = "#22c55e";
                    else if (item.type === "warning" || item.type === "caution") color = "#eab308";
                    else if (item.type === "critical" || item.type === "loss") color = "#ef4444";
                    else if (item.type === "info") color = "#60a5fa";
                    
                    return (
                      <span key={idx} className="ticker-item" style={{ color }}>
                        {item.text}
                        <span className="ticker-separator">|</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ticker and toast animation styles */}
      <style>{`
        .ticker-wrapper {
          width: 100%;
          overflow: hidden;
        }
        .ticker-content {
          display: inline-block;
          white-space: nowrap;
          animation: ticker-scroll 60s linear infinite;
          padding: 8px 0;
        }
        .ticker-item {
          display: inline;
          font-size: 14px;
        }
        .ticker-separator {
          display: inline-block;
          margin: 0 24px;
          color: #4a2a2a;
        }
        @keyframes ticker-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .ticker-content:hover {
          animation-play-state: paused;
        }
        .consequence-toast {
          animation: toast-slide-in 0.3s ease-out;
          transition: opacity 0.2s ease-out, transform 0.2s ease-out;
        }
        .consequence-toast:hover {
          opacity: 0.9;
          transform: scale(1.02);
        }
        @keyframes toast-slide-in {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StatBadge({ value, label, color }) {
  return (
    <div className="flex items-center gap-2">
      <div>
        <div className="text-sm font-bold" style={{ color }}>{value}</div>
        <div className="text-xs" style={{ color: "#525252" }}>{label}</div>
      </div>
    </div>
  );
}

// Larger stat badge for the redesigned top bar (no icons)
function StatBadgeLarge({ value, label, color }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded" style={{ backgroundColor: "#111" }}>
      <div>
        <div className="text-lg font-bold" style={{ color }}>{value}</div>
        <div className="text-xs" style={{ color: "#525252" }}>{label}</div>
      </div>
    </div>
  );
}

/**
 * EOS (Elite Operator Status) progress badge
 * Shows consecutive days progress toward EOS certification
 * Once certified, shows a golden "Elite" status
 */
function EOSStatusBadge({ worldState, tier }) {
  const { t } = useTranslation();
  const eos = worldState?.eos;
  const status = getEOSStatus(worldState);
  const consecutiveDays = eos?.consecutiveDays || 0;
  const requiredDays = EOS_REQUIREMENTS.QUALIFICATION_DAYS;
  const needsTier5 = tier < 5;

  // Show progress even before Tier 5, but indicate Tier 5 is needed
  if (needsTier5 && consecutiveDays === 0) {
    // No progress yet and not Tier 5 - show locked state
    return (
      <div className="flex items-center gap-2 opacity-50 px-3 py-2 rounded" style={{ backgroundColor: "#111" }}>
        <div>
          <div className="text-sm font-bold" style={{ color: "#525252" }}>EOS</div>
          <div className="text-xs" style={{ color: "#525252" }}>{t('ops.tier5Req')}</div>
        </div>
      </div>
    );
  }

  // Already certified - show golden Elite status
  if (status.hasEOS) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded" style={{ backgroundColor: "#111" }}>
        <div>
          <div className="text-sm font-bold" style={{
            color: "#fbbf24",
            textShadow: "0 0 8px rgba(251, 191, 36, 0.5)"
          }}>
            {t('ops.elite')}
          </div>
          <div className="text-xs" style={{ color: "#fbbf24", opacity: 0.8 }}>
            {t('ops.a380Unlocked')}
          </div>
        </div>
      </div>
    );
  }

  // Qualifying - show progress
  const progressPercent = (consecutiveDays / requiredDays) * 100;
  const isClose = consecutiveDays >= 10; // Getting close to 14
  const daysComplete = consecutiveDays >= requiredDays;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded" style={{ backgroundColor: "#111" }}>
      <div>
        <div className="text-sm font-bold" style={{ color: isClose ? "#60ffd0" : "#6366f1" }}>
          {consecutiveDays}/{requiredDays}
        </div>
        <div className="text-xs" style={{ color: needsTier5 ? "#ef4444" : "#525252" }}>
          {needsTier5 && daysComplete ? t('ops.needsTier5') : t('ops.eosDays')}
        </div>
        {/* Mini progress bar */}
        <div className="w-12 h-1 rounded-full mt-0.5" style={{ backgroundColor: "#1a1a1a" }}>
          <div
            className="h-1 rounded-full transition-all duration-300"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: needsTier5 ? "#ef4444" : (isClose ? "#60ffd0" : "#6366f1")
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ScheduledFlightRow({ flight, gameTime, airportWeather }) {
  const navigate = useNavigate();
  const minUntil = getGameMinutesUntilDeparture(flight.scheduledDeparture, gameTime);
  const needsPreflight = flight.status === "scheduled" && !flight.isAutoGenerated;
  const isGroundOps = minUntil <= 35 && minUntil > -10;
  const isCancelled = flight.status === "cancelled" || flight.phase === "cancelled";

  // Get weather impact at origin
  const originWeather = airportWeather?.[flight.origin];
  const weatherImpact = originWeather?.impact;
  const hasWeatherIssue = weatherImpact && weatherImpact.severity !== 'clear';
  
  // Cancelled flight styling
  if (isCancelled) {
    return (
      <div className="flex items-center justify-between p-3 rounded" 
        style={{ 
          backgroundColor: "#1a0a0a", 
          borderLeft: "4px solid #ef4444",
          opacity: 0.8,
        }}>
        <div className="flex items-center gap-3">
          <span className="font-bold line-through" style={{ color: "#737373" }}>{flight.flightNumber}</span>
          <span className="line-through" style={{ color: "#525252" }}>{flight.origin} - {flight.destination}</span>
          <Badge style={{ backgroundColor: "#ef4444", color: "#fff" }}>CANCELLED</Badge>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm line-through" style={{ color: "#525252" }}>{formatTime(flight.scheduledDeparture)}</span>
          {flight.cancellationCost && (
            <span className="text-xs" style={{ color: "#ef4444" }}>
              -${(flight.cancellationCost.totalCost / 1000).toFixed(0)}K
            </span>
          )}
        </div>
      </div>
    );
  }
  
  // Normal scheduled flight
  return (
    <div className="flex items-center justify-between p-3 rounded" style={{
      backgroundColor: "#111",
      borderLeft: hasWeatherIssue ? `4px solid ${getWeatherStatusColor(weatherImpact.severity)}` : 'none'
    }}>
      <div className="flex items-center gap-3">
        <span className="font-bold" style={{ color: "#e5e5e5" }}>{flight.flightNumber}</span>
        <span style={{ color: "#737373" }}>{flight.origin} - {flight.destination}</span>
        {['preboarding', 'boarding', 'door_closed', 'pushback', 'taxi_out'].includes(flight.phase) && (
          <Badge style={{ backgroundColor: "#3b82f6", color: "#fff" }}>{formatPhase(flight.phase)}</Badge>
        )}
        {/* Weather status badge */}
        {hasWeatherIssue && (
          <Badge
            style={{
              backgroundColor: getWeatherStatusColor(weatherImpact.severity),
              color: weatherImpact.severity === 'advisory' ? '#000' : '#fff'
            }}
            title={weatherImpact.reason || ''}
          >
            {getWeatherStatusLabel(weatherImpact.severity)}
            {weatherImpact.delayMinutes > 0 && ` +${weatherImpact.delayMinutes}m`}
          </Badge>
        )}
        {/* Weather hold indicator */}
        {flight.weatherHold && (
          <Badge style={{ backgroundColor: "#ef4444", color: "#fff" }}>
            WEATHER HOLD
          </Badge>
        )}
        {/* Warning if getting close to cancellation */}
        {minUntil < 0 && minUntil > -30 && !flight.weatherHold && (
          <Badge style={{ backgroundColor: "#eab308", color: "#000" }}>
            LATE T+{Math.abs(Math.floor(minUntil))}m
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm" style={{ color: "#60ffd0" }}>{formatTime(flight.scheduledDeparture)}</span>
        <span className="text-xs" style={{ color: minUntil < 0 ? "#ef4444" : minUntil < 10 ? "#eab308" : "#525252" }}>
          {minUntil > 0 ? `T-${Math.floor(minUntil)} min` : `T+${Math.abs(Math.floor(minUntil))} min`}
        </span>
        {needsPreflight && minUntil > 45 && (
          <Button size="sm" onClick={() => navigate(`/preflight?id=${flight.id}`)} style={{ backgroundColor: "#60ffd0", color: "#000" }}>
            Preflight
          </Button>
        )}
      </div>
    </div>
  );
}

function AirborneFlightRow({ flight }) {
  const isCancelled = flight.status === "cancelled" || flight.phase === "cancelled";
  
  // Determine border color
  let borderColor = "#3b82f6"; // default blue for in-flight
  if (flight.phase === "completed") borderColor = "#22c55e"; // green for completed
  if (isCancelled) borderColor = "#ef4444"; // red for cancelled
  
  return (
    <div className="flex items-center justify-between p-3 rounded" 
      style={{ 
        backgroundColor: isCancelled ? "#1a0a0a" : "#111", 
        borderLeft: `3px solid ${borderColor}`,
        opacity: isCancelled ? 0.7 : 1,
      }}>
      <div className="flex items-center gap-3">
        <span className={`font-bold ${isCancelled ? 'line-through' : ''}`} style={{ color: isCancelled ? "#737373" : "#e5e5e5" }}>
          {flight.flightNumber}
        </span>
        <span className={isCancelled ? 'line-through' : ''} style={{ color: "#737373" }}>
          {flight.origin} - {flight.destination}
        </span>
        {isCancelled ? (
          <Badge style={{ backgroundColor: "#ef4444", color: "#fff" }}>CANCELLED</Badge>
        ) : flight.totalDelayMinutes > 0 && (
          <Badge style={{ backgroundColor: "#ef4444", color: "#fff" }}>+{flight.totalDelayMinutes}m</Badge>
        )}
      </div>
      <div className="flex items-center gap-3">
        {isCancelled ? (
          flight.cancellationCost && (
            <span className="text-xs" style={{ color: "#ef4444" }}>
              -${(flight.cancellationCost.totalCost / 1000).toFixed(0)}K
            </span>
          )
        ) : (
          <>
            <Badge style={{ backgroundColor: flight.phase === "completed" ? "#22c55e" : "#3b82f6", color: "#fff" }}>
              {formatPhase(flight.phase)}
            </Badge>
            <span className="font-mono text-sm" style={{ color: "#737373" }}>
              {Math.round((flight.progress || 0) * 100)}%
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function GroundOperationCard({ flight, gameTime }) {
  const { t } = useTranslation();
  const minutesUntilDeparture = getGameMinutesUntilDeparture(flight.scheduledDeparture, gameTime);

  // Determine current phase based on time until departure
  const getCurrentPhaseInfo = () => {
    for (let i = 0; i < PHASE_ORDER.length; i++) {
      const phaseKey = PHASE_ORDER[i];
      const config = GROUND_PHASES[phaseKey];
      const nextPhaseKey = PHASE_ORDER[i + 1];
      const nextConfig = nextPhaseKey ? GROUND_PHASES[nextPhaseKey] : null;

      // Phase starts at minutesBefore, ends at next phase's minutesBefore (or 0)
      const phaseStart = config.minutesBefore;
      const phaseEnd = nextConfig ? nextConfig.minutesBefore : 0;

      if (minutesUntilDeparture <= phaseStart && minutesUntilDeparture > phaseEnd) {
        const phaseDuration = phaseStart - phaseEnd;
        const timeIntoPhase = phaseStart - minutesUntilDeparture;
        const progress = phaseDuration > 0 ? timeIntoPhase / phaseDuration : 0;
        return { phaseKey, config, progress, phaseIndex: i };
      }
    }

    // If past departure (T+), show taxiout phase completed
    // Flight should be launching soon via GameLoopEngine
    if (minutesUntilDeparture <= 0) {
      return {
        phaseKey: "taxiout",
        config: GROUND_PHASES.taxiout,
        progress: 1,
        phaseIndex: PHASE_ORDER.length - 1,
        pastDeparture: true, // Flag for UI to show launching status
      };
    }

    // Not yet in ground ops window
    return null;
  };

  const currentPhaseInfo = getCurrentPhaseInfo();
  const currentCallout = currentPhaseInfo
    ? getCalloutForPhase(currentPhaseInfo.phaseKey, currentPhaseInfo.progress, flight)
    : null;

  return (
    <div className="rounded-lg p-3" style={{ backgroundColor: "#111", border: "1px solid #222" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-lg font-bold" style={{ color: "#e5e5e5" }}>{flight.flightNumber}</div>
          <div className="text-xs" style={{ color: "#737373" }}>{flight.origin} - {flight.destination}</div>
          {flight.aircraftRegistration && (
            <div className="text-xs" style={{ color: "#525252" }}>{flight.aircraftRegistration}</div>
          )}
        </div>
        <div className="text-right">
          <div className="font-mono text-lg" style={{ color: "#60ffd0" }}>{formatTime(flight.scheduledDeparture)}</div>
          <div className="text-sm font-bold" style={{ color: minutesUntilDeparture < 5 ? "#ef4444" : minutesUntilDeparture < 10 ? "#eab308" : "#737373" }}>
            {minutesUntilDeparture > 0 ? `T-${Math.floor(minutesUntilDeparture)} min` : `T+${Math.abs(Math.floor(minutesUntilDeparture))} min`}
          </div>
        </div>
      </div>

      {/* ATC Callout Display - The main feature! */}
      {currentCallout && (
        <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#0a1520", border: "1px solid #1e3a5f" }}>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-0.5 font-bold" style={{ color: "#60a5fa" }}>ATC:</span>
            <div>
              <span className="text-sm" style={{ color: "#93c5fd", lineHeight: 1.4 }}>
                "{currentCallout}"
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Phase Progress Bar */}
      <div className="flex gap-1 mb-2">
        {PHASE_ORDER.map((phaseKey, index) => {
          const config = GROUND_PHASES[phaseKey];
          const isCompleted = currentPhaseInfo && index < currentPhaseInfo.phaseIndex;
          const isActive = currentPhaseInfo && index === currentPhaseInfo.phaseIndex;
          const isUpcoming = !currentPhaseInfo || index > currentPhaseInfo.phaseIndex;

          return (
            <div key={phaseKey} className="flex-1">
              <div
                className="p-2 rounded text-center"
                style={{
                  backgroundColor: isCompleted ? "#0a2010" : isActive ? config.bgColor : "#0a0a0a",
                  border: `1px solid ${isCompleted ? "#22c55e" : isActive ? config.color : "#1a1a1a"}`,
                  opacity: isUpcoming ? 0.3 : 1,
                }}
              >
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs font-bold" style={{ color: isCompleted ? "#22c55e" : isActive ? config.color : "#404040" }}>
                    {isCompleted ? "OK" : config.abbrev}
                  </span>
                  <span className="text-xs font-medium" style={{ color: isCompleted ? "#22c55e" : isActive ? config.color : "#404040" }}>
                    {config.name}
                  </span>
                </div>
                {/* Progress bar for active phase */}
                {isActive && currentPhaseInfo && (
                  <div className="mt-1 w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: "#222" }}>
                    <div
                      className="h-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(100, currentPhaseInfo.progress * 100)}%`,
                        backgroundColor: config.color
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current status text */}
      {currentPhaseInfo && (
        <div className="text-center text-xs" style={{ color: currentPhaseInfo.config.color }}>
          {currentPhaseInfo.config.name} {t('ops.inProgress')}
        </div>
      )}

      {/* Delay indicator - shows when flight is past scheduled departure */}
      {minutesUntilDeparture < 0 && (
        <div className="mt-2 p-2 rounded text-center" style={{ backgroundColor: "#0a2a1a", border: "1px solid #22c55e" }}>
          <span className="text-sm font-bold" style={{ color: "#22c55e" }}>
            {t('ops.launching')}
          </span>
        </div>
      )}
    </div>
  );
}